import { TcoService } from '@amzn/awscat-portfolio-assessment-service-client';
import { HostType, OnPremComputeCostOutput, OnPremOutput } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Button, Container, Header, SpaceBetween, Spinner, Table, Tabs, TabsProps } from '@amzn/awsui-components-react';
import { useContext, useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router';
import { useIsMounted } from 'react-tidy';

import { HelpPanelTopicOptions } from '../../../../common/Constants';
import { constants } from '../../../../common/Localization';
import { MatchParam } from '../../../../common/types';
import { HelpPanelContext } from '../../../../contexts/HelpPanelContext';
import { TcoContext } from '../../../../contexts/TcoContext';
import { commonTotalCostSummary, generateCashFlowTable, summaryColumnDefs } from '../../common/Common';
import HostTypeConfiguration from '../../common/HostTypeConfiguration';
import { CommonSummaryItem } from '../../common/types';
import { updateTCOAssessment } from '../../TCOHelper';
import {
    buildOnPremCashFlowItems,
    buildOnPremisesTabs,
    buildSummaryItems, 
    OnPremEnvironment, 
    OnPremFacilitiesTable,
    OnPremRackInfrastructureTable,
    OnPremSoftwareLicenseTable,
    OnPremVirtualHostTable,
} from '../OnPremisesHelper';
import { OnPremPhysicalServerTable } from './OnpremServerCostHelper';

type Props = FlashContextInterface;

const tcoService = new TcoService();
function OnpremServerCost({flash}: Props) {
    const { panelHandler } = useContext(HelpPanelContext);
    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.ONPREM_SERVER_COST);
    },[ panelHandler ]);

    const [ loading, setLoading ] = useState(false);
    const { assessment, assessmentToUpdate, tcoLoading, updateAssessment, updateTcoLoading } = useContext(TcoContext);
    const [ onPremHostType, setOnPremHostType ] = useState<HostType[] | undefined>(undefined);
    const [ onPremiseOutput, setOnPremiseOutput ] = useState<OnPremOutput | null>(null);
    const [ hostTypeConfigVisible, setHostTypeConfigVisible ] = useState(false);
    const [ activeTabId, setActiveTabId ] = useState('physicalServers');
    const [ isColocation, setIsColocation ] = useState(false);
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/tco/:tcoAssessmentId',
    });
    const tcoAssessmentId = match?.params.tcoAssessmentId ?? null;
    const portfolioId = match?.params.portfolioId ?? null;
    const isMounted = useIsMounted();
    const loadOnPremiseData = async () => {
        setLoading(true);
        try {
            if (tcoAssessmentId && tcoAssessmentId !== 'default' && assessment) {
                const onPremAssessment = await tcoService.getOnPremise(tcoAssessmentId);
                if (isMounted()) {
                    setOnPremiseOutput(onPremAssessment.output.onPremises);
                    setOnPremHostType(onPremAssessment.input.onPremises.hostTypes);
                    setIsColocation(onPremAssessment.configuration.tcoAssumptions.onpremEnvironment === OnPremEnvironment.COLO);
                }
            }
        } catch(err) {
            flash.alert(FlashType.error, (err as Error).message, constants.ERROR_LOADING_ONPREM);
        } finally {
            if (isMounted()) { setLoading(false); }
        }
    };

    useEffect(() => {
        if (assessment) {
            loadOnPremiseData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ assessment ]);

    const buildComputeCostSummaryItems = (): CommonSummaryItem[] => {
        if (!onPremiseOutput?.computeCost) { return []; }
        return buildSummaryItems(onPremiseOutput.computeCost);
    };

    const saveHostTypes = async (hostTypes: HostType[]): Promise<void> => {
        try {
            updateTcoLoading(true);
            if (assessmentToUpdate && portfolioId) {
                assessmentToUpdate.input.onPremises.hostTypes = hostTypes;
                const newTcoAssessment = await updateTCOAssessment(portfolioId, assessmentToUpdate, tcoService);
                updateAssessment(newTcoAssessment);
            }
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.ERROR_UPDATING_ASSESSMENT);
        } finally {
            updateTcoLoading(false);
        }
    };

    const buildTabs = (computeCost: OnPremComputeCostOutput, currency: string): TabsProps.Tab[] => {
        const virtualHostContent = (
            <SpaceBetween size='l' direction='vertical'>
                <Button variant='primary' onClick={() => setHostTypeConfigVisible(true)} id='btn-onprem-server-hosttype' > 
                    {constants.ON_PREM_CONFIGURE_HOST_TYPES} 
                </Button>
                {hostTypeConfigVisible && onPremHostType && (
                    <HostTypeConfiguration
                        visible={hostTypeConfigVisible}
                        setVisible={setHostTypeConfigVisible}
                        hostTypes={onPremHostType}
                        saveHostTypes={saveHostTypes}
                    />
                )}
                {OnPremVirtualHostTable(computeCost.virtual.hosts, currency)}
            </SpaceBetween>
        );

        return buildOnPremisesTabs(
            OnPremPhysicalServerTable(computeCost.physical.servers, currency),
            virtualHostContent,
            OnPremRackInfrastructureTable(computeCost.hardware, currency),
            OnPremSoftwareLicenseTable(computeCost.software, currency),
            OnPremFacilitiesTable(computeCost.facilities, currency, isColocation),
            generateCashFlowTable(buildOnPremCashFlowItems(computeCost.cashFlow), computeCost.cashFlow.yearly.total.length, currency),
        );
    };
    
    const buildOnPremPageContent = (): JSX.Element => {
        if (tcoLoading || loading || !onPremiseOutput) {
            return (
                <Spinner size='big'/>
            );
        }
        const computeCost = onPremiseOutput.computeCost;
        const currency = onPremiseOutput.currency;
        return (
            <SpaceBetween size='xl'>
                <Header variant='h1'>{constants.ON_PREM_SERVER_TITLE}</Header>
                <Table
                    columnDefinitions={summaryColumnDefs(currency)}
                    items={buildComputeCostSummaryItems()}
                    sortingDisabled
                />
                {commonTotalCostSummary(currency, computeCost.totalCost, computeCost.manualAdjustment.value, computeCost.adjustedTotalCost)}
                <Header variant='h2'>{constants.DETAILS}</Header>
                <Tabs tabs={buildTabs(computeCost, currency)} activeTabId={activeTabId} onChange={({ detail }) => setActiveTabId(detail.activeTabId)}></Tabs>
            </SpaceBetween>
        );
    };

    return (
        <Container data-testid='container'>
            {buildOnPremPageContent()}
        </Container>
    );
}

export default withFlashContext(OnpremServerCost);
